import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../utils/apiUtils';
import MetricsCards from './components/MetricsCards';
import UsageAnalytics from './components/UsageAnalytics';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',
  orange: '#ffaa01',
  pink: '#df3e99'
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [timeframe, setTimeframe] = useState('today');
  const [dashboardData, setDashboardData] = useState({
    metrics: null,
    usageTrends: null,
    featureUsage: null,
    jobsByType: null
  });

  // Real-time update interval (30 seconds)
  const UPDATE_INTERVAL = 30000;

  const fetchDashboardData = async () => {
    try {
      setError('');

      // Fetch all dashboard data in parallel
      const promises = [
        apiGet(`/api/usage/admin/dashboard/metrics?timeframe=${timeframe}`),
        apiGet(`/api/usage/admin/dashboard/usage-trends?timeframe=${timeframe}`),
        apiGet(`/api/usage/admin/dashboard/feature-usage?timeframe=${timeframe}`),
        apiGet(`/api/usage/admin/dashboard/jobs-by-type?timeframe=${timeframe}`)
      ];

      const results = await Promise.allSettled(promises);

      const [metricsResult, trendsResult, featureResult, jobsByTypeResult] = results;

      // Process results
      const newData = {
        metrics: metricsResult.status === 'fulfilled' ? await metricsResult.value.json() : null,
        usageTrends: trendsResult.status === 'fulfilled' ? await trendsResult.value.json() : null,
        featureUsage: featureResult.status === 'fulfilled' ? await featureResult.value.json() : null,
        jobsByType: jobsByTypeResult.status === 'fulfilled' ? await jobsByTypeResult.value.json() : null
      };

      // Check for errors in responses
      const errors = [];
      Object.entries(newData).forEach(([key, result]) => {
        if (result && !result.success) {
          errors.push(`${key}: ${result.error}`);
        }
      });

      if (errors.length > 0) {
        setError(`Some data failed to load: ${errors.join(', ')}`);
      }

      setDashboardData(newData);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(`Failed to fetch dashboard data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Set up real-time updates
    const interval = setInterval(fetchDashboardData, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [timeframe]);

  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value);
  };

  const formatLastUpdated = () => {
    const seconds = Math.floor((new Date() - lastUpdated) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} sx={{ color: wyspurColors.blue }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', color: 'white' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ color: wyspurColors.blue, fontWeight: 600, mb: 1 }}>
          Admin Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Real-time system monitoring and analytics
          </Typography>
          <Chip 
            label={`Last updated: ${formatLastUpdated()}`}
            size="small"
            variant="outlined"
            sx={{ 
              color: wyspurColors.orange, 
              borderColor: wyspurColors.orange,
              backgroundColor: 'rgba(255, 170, 1, 0.1)'
            }}
          />
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            backgroundColor: `${wyspurColors.orange}15`,
            border: `1px solid ${wyspurColors.orange}40`,
            color: wyspurColors.orange,
            '& .MuiAlert-icon': { color: wyspurColors.orange }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Date Filter */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ color: wyspurColors.blue, fontWeight: 600 }}>
          Key Metrics
        </Typography>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Time Period</InputLabel>
          <Select
            value={timeframe}
            onChange={handleTimeframeChange}
            label="Time Period"
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: wyspurColors.blue
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: wyspurColors.blue
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'rgba(15, 113, 251, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(15, 113, 251, 0.2)',
                }
              }
            }}
          >
            <MenuItem value="today" sx={{ color: 'white' }}>Today</MenuItem>
            <MenuItem value="7days" sx={{ color: 'white' }}>Last 7 Days</MenuItem>
            <MenuItem value="30days" sx={{ color: 'white' }}>Last 30 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics Cards */}
      <MetricsCards data={dashboardData.metrics} />

      {/* Usage Analytics */}
      <UsageAnalytics
        usageTrends={dashboardData.usageTrends}
        featureUsage={dashboardData.featureUsage}
        jobsByType={dashboardData.jobsByType}
        timeframe={timeframe}
      />
    </Box>
  );
};

export default AdminDashboard;